import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen, Brain, BriefcaseBusiness, ChevronDown, CircleUserRound, Cloud, FolderKanban, GraduationCap,
  Home, Library, Menu, NotebookPen, PackageOpen, Search, Settings, ShoppingBag, Sparkles, Wrench, X,
} from "lucide-react";
import { Dialog } from "../ui/Dialog";
import { useAuth } from "../../contexts/AuthContext";
import { useOnline } from "../../hooks/useOnline";

const workItems = [
  { to: "/work/tasks", label: "工作任务", icon: BriefcaseBusiness },
  { to: "/work/notes", label: "工作笔记", icon: NotebookPen },
  { to: "/work/memories", label: "工作记忆", icon: Brain },
  { to: "/work/tools", label: "工具收藏", icon: Wrench },
  { to: "/work/purchases", label: "购买记录", icon: ShoppingBag },
];
const growthItems = [
  { to: "/growth/projects", label: "学习项目", icon: FolderKanban },
  { to: "/growth/notes", label: "学习笔记", icon: BookOpen },
  { to: "/growth/memories", label: "个人记忆", icon: Brain },
  { to: "/growth/cards", label: "记忆卡片", icon: Library },
  { to: "/growth/reviews", label: "每日复盘", icon: Sparkles },
];

function Brand() {
  return (
    <NavLink to="/dashboard" className="flex min-h-16 items-center gap-3 px-5 font-bold">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-sm text-[var(--color-on-primary)]" aria-hidden="true">T</span>
      <span>托尼的个人工作台</span>
    </NavLink>
  );
}

function NavItem({ to, label, icon: Icon, onClick }: { to: string; label: string; icon: typeof Home; onClick?: () => void }) {
  return (
    <NavLink to={to} onClick={onClick} className={({ isActive }) => `relative flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${isActive ? "bg-[var(--color-primary-soft)] text-primary before:absolute before:-left-2 before:h-6 before:w-[3px] before:rounded-full before:bg-primary" : "text-muted hover:bg-subtle hover:text-ink"}`}>
      <Icon size={19} aria-hidden="true" /><span>{label}</span>
    </NavLink>
  );
}

function Navigation({ onClick }: { onClick?: () => void }) {
  return (
    <nav aria-label="主要导航" className="space-y-5 px-3 py-3">
      <NavItem to="/dashboard" label="首页" icon={Home} onClick={onClick} />
      <section>
        <h2 className="px-3 pb-2 text-xs font-bold uppercase tracking-wider text-[var(--color-text-subtle)]">工作处理</h2>
        <div className="space-y-1">{workItems.map((item) => <NavItem key={item.to} {...item} onClick={onClick} />)}</div>
      </section>
      <section>
        <h2 className="px-3 pb-2 text-xs font-bold uppercase tracking-wider text-[var(--color-text-subtle)]">个人成长</h2>
        <div className="space-y-1">{growthItems.map((item) => <NavItem key={item.to} {...item} onClick={onClick} />)}</div>
      </section>
    </nav>
  );
}

function MobileNav() {
  const items = [
    { to: "/dashboard", label: "首页", icon: Home }, { to: "/work", label: "工作", icon: BriefcaseBusiness },
    { to: "/growth", label: "成长", icon: GraduationCap }, { to: "/search", label: "搜索", icon: Search },
    { to: "/settings/account", label: "更多", icon: Menu },
  ];
  return (
    <nav className="mobile-safe-bottom fixed inset-x-0 bottom-0 z-20 border-t bg-surface px-2 pt-2 lg:hidden" aria-label="移动端导航">
      <div className="mx-auto flex max-w-xl justify-around">{items.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} className={({ isActive }) => `flex min-h-14 min-w-14 flex-col items-center justify-center gap-0.5 rounded-lg px-2 text-xs ${isActive ? "text-primary" : "text-muted"}`}>
          <Icon size={20} aria-hidden="true" /><span>{label}</span>
        </NavLink>
      ))}</div>
    </nav>
  );
}

const pageTitles: Array<[RegExp, string]> = [
  [/^\/dashboard/, "首页"], [/^\/work\/tasks/, "工作任务"], [/^\/work\/notes/, "工作笔记"], [/^\/work\/memories/, "工作记忆"],
  [/^\/work\/tools/, "工具收藏"], [/^\/work\/purchases/, "购买记录"], [/^\/growth\/projects/, "学习项目"],
  [/^\/growth\/notes/, "学习笔记"], [/^\/growth\/memories/, "个人记忆"], [/^\/growth\/cards/, "记忆卡片"], [/^\/growth\/reviews/, "每日复盘"],
  [/^\/search/, "全局搜索"], [/^\/settings/, "设置"], [/^\/work$/, "工作处理"], [/^\/growth$/, "个人成长"],
];

export function AppShell() {
  const [drawer, setDrawer] = useState(false);
  const { expired, dismissExpired } = useAuth();
  const online = useOnline();
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitles.find(([pattern]) => pattern.test(location.pathname))?.[1] ?? "托尼的个人工作台";
  useEffect(() => { setDrawer(false); }, [location.pathname]);
  useEffect(() => { document.title = `${title} · 托尼的个人工作台`; }, [title]);
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k" && !["INPUT", "TEXTAREA"].includes(target.tagName)) {
        event.preventDefault(); navigate("/search");
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <a href="#main-content" className="fixed left-4 top-2 z-[100] -translate-y-20 rounded-lg bg-primary px-4 py-2 text-[var(--color-on-primary)] focus:translate-y-0">跳到主要内容</a>
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 border-r bg-surface lg:flex lg:flex-col">
        <Brand /><div className="min-h-0 flex-1 overflow-y-auto"><Navigation /></div>
        <div className="border-t p-3"><NavItem to="/settings/account" label="设置" icon={Settings} /></div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-surface/95 px-4 backdrop-blur sm:px-6 lg:px-8">
          <button className="icon-btn lg:hidden" onClick={() => setDrawer(true)} aria-label="打开导航"><Menu size={21} /></button>
          <div className="min-w-0 flex-1"><p className="truncate font-semibold">{title}</p></div>
          <NavLink to="/search" className="btn-secondary hidden max-w-72 flex-1 justify-start text-muted sm:flex"><Search size={17} /><span className="truncate">搜索工作台</span><kbd className="ml-auto rounded border px-1.5 py-0.5 text-xs">Ctrl K</kbd></NavLink>
          <span className={`hidden items-center gap-1.5 text-xs font-medium sm:flex ${online ? "text-success" : "text-danger"}`}><Cloud size={16} />{online ? "已连接" : "离线"}</span>
          <NavLink to="/settings/account" className="icon-btn" aria-label="账号设置"><CircleUserRound size={21} /></NavLink>
        </header>
        {!online ? <div className="sticky top-16 z-10 bg-[var(--color-warning-soft)] px-4 py-2 text-center text-sm font-medium text-warning" role="status">当前离线，可以查看本页内容，但不能保存更改。恢复网络后请重试。</div> : null}
        <main id="main-content" tabIndex={-1}><Outlet /></main>
      </div>

      {drawer ? (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="导航菜单">
          <button className="absolute inset-0 bg-[#0f1420]/60" onClick={() => setDrawer(false)} aria-label="关闭导航" />
          <aside className="relative flex h-full w-[min(20rem,86vw)] flex-col bg-surface shadow-[var(--shadow-dialog)]">
            <div className="flex items-center border-b"><div className="flex-1"><Brand /></div><button className="icon-btn mr-3" onClick={() => setDrawer(false)} aria-label="关闭导航"><X size={20} /></button></div>
            <div className="overflow-y-auto"><Navigation onClick={() => setDrawer(false)} /></div>
          </aside>
        </div>
      ) : null}
      <MobileNav />

      <Dialog open={expired} title="登录已过期" description="为保护你的工作记录，需要重新登录。未提交的页面内容会保留在当前内存中。" onClose={() => undefined} preventClose footer={
        <button className="btn-primary" onClick={() => { dismissExpired(); navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`, { replace: true }); }}>重新登录</button>
      }><p className="text-sm leading-relaxed text-muted">重新登录后请先检查云端版本，再继续保存当前修改。</p></Dialog>
    </div>
  );
}
